import { useEffect, useRef } from 'react';
import { AppRouter } from '@/routing/app-routing';
import { ThemeProvider } from 'next-themes';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter } from 'react-router-dom';
import { LoadingBarContainer } from 'react-top-loading-bar';
import { Toaster } from 'sonner';
import { QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { queryClient } from '@marine/api-client/query-client';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ThemeSync } from './components/theme-sync';

// Router basename is "/" — the app serves at the domain root; /build/ is only
// the asset base (manifest-driven). import.meta.env.BASE_URL is /build/ in
// production builds, which would make the router fail to match "/".

/**
 * Invalidates the platform `['me']` and tenant `['currentEmployee']` TanStack
 * Queries when the window regains focus so preferences written on another
 * device/tab propagate back. Throttled to at most one refetch per 30 s. When
 * neither query is registered (e.g. the user is logged out) the invalidation
 * is a silent no-op.
 */
function PreferencesFocusSync() {
  const client = useQueryClient();
  const lastRefetch = useRef(0);

  useEffect(() => {
    const THROTTLE_MS = 30_000;
    const handler = () => {
      const now = Date.now();
      if (now - lastRefetch.current < THROTTLE_MS) return;
      lastRefetch.current = now;
      client.invalidateQueries({ queryKey: ['me'] });
      client.invalidateQueries({ queryKey: ['currentEmployee'] });
    };

    window.addEventListener('focus', handler);
    return () => window.removeEventListener('focus', handler);
  }, [client]);

  return null;
}

export function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider
          attribute="class"
          defaultTheme="light"
          storageKey="marine-dms-theme"
          enableSystem
          disableTransitionOnChange
          enableColorScheme
        >
          <HelmetProvider>
            <LoadingBarContainer>
              <BrowserRouter basename="/">
                {/*
                  Sonner Toaster:
                  - position="top-right" — out of geometric collision with
                    bottom-right wizard navigation (Next button overlap was
                    the root cause of the E2E flake exposed by PR #113).
                  - zIndex: 99999 — preserves the Apr 8 z-index fix
                    (commit 1254d4b1) for the original "buttons appearing
                    above the toast" problem.
                  - No pointerEvents override: Sonner's defaults already
                    have container `pointer-events: none` (empty area is
                    click-through) and each toast `pointer-events: auto`
                    (toast itself blocks clicks). The Apr 8 override was
                    redundant — the real fix back then was just z-index.
                  - notistack removed in the same commit: useAuth +
                    useTenantAuth migrated to `toast.info(...)`.
                */}
                <Toaster
                  position="top-right"
                  style={{ zIndex: 99999 }}
                  containerAriaLabel="Notifications"
                />
                <PreferencesFocusSync />
                <ThemeSync />
                <AppRouter />
              </BrowserRouter>
            </LoadingBarContainer>
          </HelmetProvider>
        </ThemeProvider>
        {import.meta.env.DEV && import.meta.env.VITE_ENABLE_RQ_DEVTOOLS === 'true' ? (
          <ReactQueryDevtools initialIsOpen={false} />
        ) : null}
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
